import type { MeResponse, Permission, SessionResponse } from '@zenarty/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, onSessionLost, refreshSession, setAccessToken } from './api';

type Status = 'loading' | 'anonymous' | 'authenticated';

interface AuthState {
  status: Status;
  me: MeResponse | null;
  can: (permission: Permission) => boolean;
  /** Finishes a login (after MFA) or a refresh. */
  establish: (session: SessionResponse) => Promise<void>;
  reloadMe: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [me, setMe] = useState<MeResponse | null>(null);

  const reloadMe = useCallback(async () => {
    setMe(await api<MeResponse>('/me'));
    setStatus('authenticated');
  }, []);

  const establish = useCallback(
    async (session: SessionResponse) => {
      setAccessToken(session.accessToken);
      await reloadMe();
    },
    [reloadMe],
  );

  const reset = useCallback(() => {
    setAccessToken(null);
    setMe(null);
    setStatus('anonymous');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      reset();
    }
  }, [reset]);

  // Resume a session from the refresh cookie on page load.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const ok = await refreshSession();
      if (cancelled) return;
      if (ok) await reloadMe().catch(reset);
      else reset();
    })();
    const off = onSessionLost(reset);
    return () => {
      cancelled = true;
      off();
    };
  }, [reloadMe, reset]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      me,
      can: (p) => Boolean(me?.permissions.includes(p)),
      establish,
      reloadMe,
      logout,
    }),
    [status, me, establish, reloadMe, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
